import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import {
  cancelVendorDirectoryCollectionRun,
  getVendorDirectoryCollectionRun,
  getVendorDirectoryCollectionSourceRecord,
  listVendorDirectoryCollectionPlans,
  listVendorDirectoryCollectionRunRecords,
  listVendorDirectoryCollectionRuns,
  retryVendorDirectoryCollectionRun,
  startVendorDirectoryCollectionRun,
} from "../../api/clientVendorDirectoryCollectionApi";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.collectionRuns";

const ACTIVE_STATUSES = new Set([
  "queued",
  "initializing",
  "running",
  "rate_limited",
  "cancel_requested",
  "paused",
]);

const STATUS_FILTERS = [
  "",
  "queued",
  "running",
  "completed",
  "completed_with_errors",
  "failed",
  "cancelled",
  "blocked",
  "rate_limited",
];

function runBadgeClass(status) {
  if (status === "completed") return "cvd-badge--published";
  if (["failed", "cancelled", "blocked"].includes(status)) return "cvd-badge--suspended";
  if (["running", "completed_with_errors", "rate_limited", "cancel_requested"].includes(status)) {
    return "cvd-badge--warning";
  }
  if (["queued", "initializing", "paused"].includes(status)) return "cvd-badge--draft";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function shortId(id) {
  if (!id) return "—";
  return `${String(id).slice(0, 8)}…`;
}

function RunStatusBadge({ status, t }) {
  return (
    <span className={`cvd-badge ${runBadgeClass(status)}`}>
      {t(`${COPY_PREFIX}.status.${status}`, {}, status)}
    </span>
  );
}

function MetricCard({ label, value, hint }) {
  return (
    <article className="cvd-metric-card">
      <span className="cvd-metric-card__label">{label}</span>
      <strong className="cvd-metric-card__value">{value ?? 0}</strong>
      {hint ? <span className="cvd-metric-card__hint">{hint}</span> : null}
    </article>
  );
}

function PipelineBar({ label, value, max, tone = "blue" }) {
  const safeMax = Math.max(max, 1);
  const pct = Math.min(100, Math.round((value / safeMax) * 100));
  return (
    <div className="cvd-run-pipeline__row">
      <div className="cvd-run-pipeline__head">
        <span>{label}</span>
        <strong>{value ?? 0}</strong>
      </div>
      <div className="cvd-run-pipeline__track" aria-hidden="true">
        <div className={`cvd-run-pipeline__fill cvd-run-pipeline__fill--${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function RecordDetailField({ label, children }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children || "—"}</dd>
    </div>
  );
}

function ReceivedRecordDetailModal({ clientViewId, recordId, onClose, t, locale }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const closeButtonRef = useRef(null);
  const titleId = "cvd-run-record-title";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorMessage("");
    getVendorDirectoryCollectionSourceRecord(clientViewId, recordId)
      .then((body) => {
        if (!cancelled) setRecord(body.record || body);
      })
      .catch((error) => {
        if (!cancelled) setErrorMessage(error.message || t(`${COPY_PREFIX}.recordLoadError`));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId, recordId, t]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const rawPayload = record?.rawPayload && Object.keys(record.rawPayload).length
    ? JSON.stringify(record.rawPayload, null, 2)
    : null;

  return createPortal(
    <div
      className="cvd-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="cvd-modal cvd-run-record-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="cvd-modal__header">
          <div>
            <p className="cvd-modal__eyebrow">{t(`${COPY_PREFIX}.recordModalEyebrow`)}</p>
            <h2 id={titleId}>{record?.rawName || t(`${COPY_PREFIX}.recordModalTitle`)}</h2>
            <p className="muted">{recordId ? shortId(recordId) : ""}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="cvd-btn cvd-btn--ghost"
            onClick={onClose}
          >
            {t(`${COPY_PREFIX}.recordModalClose`)}
          </button>
        </header>
        <div className="cvd-modal__body">
          {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.recordsLoading`)} /> : null}
          {!loading && errorMessage ? <ClientPortalError message={errorMessage} /> : null}
          {!loading && !errorMessage && record ? (
            <>
              <dl className="cvd-dl cvd-run-record-modal__dl">
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.category`)}>
                  {record.rawCategory}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.municipality`)}>
                  {record.rawMunicipality}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.phone`)}>
                  {record.rawPhone}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.email`)}>
                  {record.rawEmail}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.website`)}>
                  {record.rawWebsite ? (
                    <a href={record.rawWebsite} target="_blank" rel="noreferrer">
                      {record.rawWebsite}
                    </a>
                  ) : null}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.address`)}>
                  {record.rawAddress}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.neighborhood`)}>
                  {record.rawPayload?.neighborhoodName}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.geocode`)}>
                  {Number.isFinite(Number(record.rawPayload?.latitude)) &&
                  Number.isFinite(Number(record.rawPayload?.longitude))
                    ? `${Number(record.rawPayload.latitude).toFixed(6)}, ${Number(record.rawPayload.longitude).toFixed(6)}`
                    : null}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.sourceUrl`)}>
                  {record.sourceUrl ? (
                    <a href={record.sourceUrl} target="_blank" rel="noreferrer">
                      {record.sourceUrl}
                    </a>
                  ) : null}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.sourceRecordId`)}>
                  {record.sourceRecordId}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.parseStatus`)}>
                  {record.parseStatus}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.changeStatus`)}>
                  {record.changeStatus}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.table.observed`)}>
                  {formatDateTime(record.observedAt, locale)}
                </RecordDetailField>
                <RecordDetailField label={t(`${COPY_PREFIX}.recordDetail.lastSeen`)}>
                  {formatDateTime(record.lastSeenAt, locale)}
                </RecordDetailField>
              </dl>
              {rawPayload ? (
                <details className="cvd-run-record-modal__raw">
                  <summary>{t(`${COPY_PREFIX}.recordDetail.rawPayload`)}</summary>
                  <pre>{rawPayload}</pre>
                </details>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function CollectionRunDetail({
  run,
  records,
  recordsLoading,
  basePath,
  clientViewId,
  selectedRecordId,
  onSelectRecord,
  onCloseRecord,
  t,
  locale,
  onRetry,
  onCancel,
  actionPending,
}) {
  const pipelineMax = Math.max(
    run.recordsReceived || 0,
    run.recordsParsed || 0,
    run.recordsStaged || 0,
    1
  );
  const canCancel = ["queued", "running", "initializing", "rate_limited"].includes(run.status);
  const canRetry = ["failed", "completed_with_errors", "cancelled"].includes(run.status);
  const candidatesBase = `${basePath.replace(/\/colecciones\/ejecuciones.*$/, "")}/candidatos`;
  const stagedCandidatesPath =
    run.recordsStaged > 0 ? `${candidatesBase}?collectionRunId=${run.id}` : null;
  const skippedOnly = (run.recordsStaged || 0) === 0 && (run.recordsSkipped || 0) > 0;

  const outcomeBadgeClass = (outcome) => {
    if (outcome === "staged" || outcome === "received") return "cvd-badge--published";
    if (outcome === "rejected") return "cvd-badge--suspended";
    if (outcome === "skipped_unchanged" || outcome === "skipped_existing_candidate") {
      return "cvd-badge--warning";
    }
    return "cvd-badge--warning";
  };

  return (
    <div className="cvd-run-detail">
      <div className="cvd-admin__header">
        <div>
          <p className="cvd-run-detail__eyebrow">{run.sourceName || run.planName || shortId(run.id)}</p>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.detailTitle`)}</h1>
          <div className="cvd-run-detail__meta">
            <RunStatusBadge status={run.status} t={t} />
            <span className="muted">{formatDateTime(run.startedAt || run.createdAt, locale)}</span>
          </div>
        </div>
        <div className="cvd-admin__actions">
          <Link className="cvd-btn cvd-btn--ghost" to={basePath}>
            ← {t(`${COPY_PREFIX}.backToList`)}
          </Link>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_RETRY}>
            {canRetry ? (
              <button
                type="button"
                className="cvd-btn cvd-btn--secondary"
                disabled={actionPending}
                onClick={() => onRetry(run.id)}
              >
                {t(`${COPY_PREFIX}.retry`)}
              </button>
            ) : null}
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_CANCEL}>
            {canCancel ? (
              <button
                type="button"
                className="cvd-btn cvd-btn--danger"
                disabled={actionPending}
                onClick={() => onCancel(run.id)}
              >
                {t(`${COPY_PREFIX}.cancel`)}
              </button>
            ) : null}
          </VendorDirectoryPermissionGate>
        </div>
      </div>

      {run.errorSummary ? (
        <div className="cvd-alert cvd-alert--warning" role="alert">
          {run.errorSummary}
        </div>
      ) : null}

      <div className="cvd-metric-grid">
        <MetricCard
          label={t(`${COPY_PREFIX}.detail.requests`)}
          value={`${run.requestsSucceeded ?? 0}/${run.requestsAttempted ?? 0}`}
        />
        <MetricCard label={t(`${COPY_PREFIX}.detail.recordsReceived`)} value={run.recordsReceived} />
        <MetricCard label={t(`${COPY_PREFIX}.detail.recordsStaged`)} value={run.recordsStaged} />
        <MetricCard label={t(`${COPY_PREFIX}.detail.recordsRejected`)} value={run.recordsRejected} />
      </div>

      <div className="cvd-run-detail__grid">
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.progress`)}</h2>
          <dl className="cvd-dl">
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.started`)}</dt>
              <dd>{formatDateTime(run.startedAt, locale)}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.completed`)}</dt>
              <dd>{formatDateTime(run.completedAt || run.failedAt || run.cancelledAt, locale)}</dd>
            </div>
            {run.runNote ? (
              <div>
                <dt>{t(`${COPY_PREFIX}.detail.note`)}</dt>
                <dd>{run.runNote}</dd>
              </div>
            ) : null}
          </dl>
          <div className="cvd-run-pipeline">
            <h3>{t(`${COPY_PREFIX}.detail.pipeline`)}</h3>
            <PipelineBar
              label={t(`${COPY_PREFIX}.detail.recordsReceived`)}
              value={run.recordsReceived}
              max={pipelineMax}
              tone="slate"
            />
            <PipelineBar
              label={t(`${COPY_PREFIX}.detail.recordsParsed`)}
              value={run.recordsParsed}
              max={pipelineMax}
              tone="blue"
            />
            <PipelineBar
              label={t(`${COPY_PREFIX}.detail.recordsStaged`)}
              value={run.recordsStaged}
              max={pipelineMax}
              tone="green"
            />
            <PipelineBar
              label={t(`${COPY_PREFIX}.detail.recordsSkipped`)}
              value={run.recordsSkipped}
              max={pipelineMax}
              tone="amber"
            />
            <PipelineBar
              label={t(`${COPY_PREFIX}.detail.recordsRejected`)}
              value={run.recordsRejected}
              max={pipelineMax}
              tone="red"
            />
          </div>
        </section>

        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.configuration`)}</h2>
          <dl className="cvd-dl">
            <div>
              <dt>{t(`${COPY_PREFIX}.table.plan`)}</dt>
              <dd>{run.planName || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.table.source`)}</dt>
              <dd>{run.sourceName || "—"}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.connector`)}</dt>
              <dd>
                {run.connectorKey} v{run.connectorVersion}
              </dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.parser`)}</dt>
              <dd>v{run.parserVersion}</dd>
            </div>
            <div>
              <dt>{t(`${COPY_PREFIX}.detail.trigger`)}</dt>
              <dd>{t(`${COPY_PREFIX}.trigger.${run.triggerType}`, {}, run.triggerType)}</dd>
            </div>
          </dl>
          <div className="cvd-run-detail__links">
            {stagedCandidatesPath ? (
              <Link className="cvd-btn cvd-btn--secondary" to={stagedCandidatesPath}>
                {t(`${COPY_PREFIX}.viewStagedCandidates`, { count: run.recordsStaged })}
              </Link>
            ) : (
              <p className="muted">{t(`${COPY_PREFIX}.noStagedCandidates`)}</p>
            )}
          </div>
        </section>
      </div>

      <section className="cvd-panel">
        <div className="cvd-run-detail__records-header">
          <div>
            <h2>{t(`${COPY_PREFIX}.recordsReceivedTitle`)}</h2>
            <p className="muted">
              {t(`${COPY_PREFIX}.recordsReceivedHint`, {
                listed: records.length,
                received: run.recordsReceived ?? 0,
              })}
            </p>
          </div>
        </div>
        {skippedOnly ? (
          <div className="cvd-alert cvd-alert--warning" role="status">
            {t(`${COPY_PREFIX}.skippedOnlyHint`, { count: run.recordsSkipped })}
          </div>
        ) : null}
        {recordsLoading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.recordsLoading`)} /> : null}
        {!recordsLoading && records.length === 0 ? (
          <p className="muted">{t(`${COPY_PREFIX}.recordsEmpty`)}</p>
        ) : null}
        {!recordsLoading && records.length > 0 ? (
          <div className="cvd-table-wrap">
            <table className="cvd-table">
              <thead>
                <tr>
                  <th>{t(`${COPY_PREFIX}.table.source`)}</th>
                  <th>{t(`${COPY_PREFIX}.recordDetail.municipality`)}</th>
                  <th>{t(`${COPY_PREFIX}.recordDetail.category`)}</th>
                  <th>{t(`${COPY_PREFIX}.table.outcome`)}</th>
                  <th>{t(`${COPY_PREFIX}.table.candidate`)}</th>
                  <th>{t(`${COPY_PREFIX}.table.observed`)}</th>
                  <th className="cvd-table__col--actions">{t(`${COPY_PREFIX}.table.actions`)}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => {
                  const candidateId = record.candidateId || record.sourceRecordCandidateId;
                  const candidatePath = candidateId
                    ? `${candidatesBase}/${candidateId}`
                    : null;
                  const sourceRecordId = record.sourceRecordId || record.id;
                  return (
                    <tr key={record.id || record.sourceRecordKey}>
                      <td>{record.rawName || record.sourceRecordKey || shortId(record.id)}</td>
                      <td>{record.rawMunicipality || "—"}</td>
                      <td>{record.rawCategory || "—"}</td>
                      <td>
                        <span className={`cvd-badge ${outcomeBadgeClass(record.outcome)}`}>
                          {t(`${COPY_PREFIX}.outcome.${record.outcome}`, {}, record.outcome)}
                        </span>
                      </td>
                      <td>
                        {candidatePath ? (
                          <Link to={candidatePath}>{t(`${COPY_PREFIX}.reviewCandidate`)}</Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{formatDateTime(record.observedAt, locale)}</td>
                      <td className="cvd-table__col--actions">
                        {sourceRecordId ? (
                          <button
                            type="button"
                            className="cvd-btn cvd-btn--ghost cvd-btn--sm"
                            onClick={() => onSelectRecord(sourceRecordId)}
                          >
                            {t(`${COPY_PREFIX}.viewRecord`)}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {selectedRecordId ? (
        <ReceivedRecordDetailModal
          clientViewId={clientViewId}
          recordId={selectedRecordId}
          onClose={onCloseRecord}
          t={t}
          locale={locale}
        />
      ) : null}
    </div>
  );
}

export default function ClientVendorDirectoryCollectionRunListPage() {
  const { clientViewId, runId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runs, setRuns] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runRecords, setRunRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerTone, setBannerTone] = useState("success");
  const [actionPending, setActionPending] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedRecordId, setSelectedRecordId] = useState("");
  const [showStartPanel, setShowStartPanel] = useState(false);
  const closeSelectedRecord = useCallback(() => setSelectedRecordId(""), []);

  const statusFilter = searchParams.get("status") || "";
  const basePath = `${serviciosBase}/colecciones/ejecuciones`;

  const loadRuns = useCallback(async () => {
    setErrorMessage("");
    try {
      const body = await listVendorDirectoryCollectionRuns(clientViewId, {
        status: statusFilter || undefined,
      });
      setRuns(body.runs || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setRuns([]);
    }
  }, [clientViewId, statusFilter, t]);

  const loadPlans = useCallback(async () => {
    try {
      const body = await listVendorDirectoryCollectionPlans(clientViewId);
      setPlans(body.plans || []);
    } catch {
      setPlans([]);
    }
  }, [clientViewId]);

  const loadRunDetail = useCallback(async () => {
    if (!runId) {
      setSelectedRun(null);
      setRunRecords([]);
      return;
    }
    try {
      const body = await getVendorDirectoryCollectionRun(clientViewId, runId);
      setSelectedRun(body.run);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setSelectedRun(null);
    }
  }, [clientViewId, runId, t]);

  const loadRunRecords = useCallback(async () => {
    if (!runId) {
      setRunRecords([]);
      return;
    }
    setRecordsLoading(true);
    try {
      const body = await listVendorDirectoryCollectionRunRecords(clientViewId, runId);
      setRunRecords(body.records || []);
    } catch {
      setRunRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  }, [clientViewId, runId]);

  useEffect(() => {
    setSelectedRecordId("");
  }, [runId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([loadRuns(), loadPlans()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRuns, loadPlans]);

  useEffect(() => {
    loadRunDetail();
    loadRunRecords();
  }, [loadRunDetail, loadRunRecords]);

  const hasActiveRuns = useMemo(
    () => runs.some((run) => ACTIVE_STATUSES.has(run.status)) || ACTIVE_STATUSES.has(selectedRun?.status),
    [runs, selectedRun?.status]
  );

  useEffect(() => {
    if (!hasActiveRuns) return undefined;
    const timer = window.setInterval(() => {
      loadRuns();
      if (runId) {
        loadRunDetail();
        loadRunRecords();
      }
    }, 8000);
    return () => window.clearInterval(timer);
  }, [hasActiveRuns, loadRunDetail, loadRunRecords, loadRuns, runId]);

  const summary = useMemo(() => {
    const active = runs.filter((run) => ACTIVE_STATUSES.has(run.status)).length;
    const failed = runs.filter((run) => ["failed", "blocked", "cancelled"].includes(run.status)).length;
    const staged = runs.reduce((sum, run) => sum + (run.recordsStaged || 0), 0);
    return { total: runs.length, active, failed, staged };
  }, [runs]);

  const runnablePlans = useMemo(
    () => plans.filter((plan) => ["active", "draft"].includes(plan.status)),
    [plans]
  );

  const setStatusFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("status", value);
    else next.delete("status");
    setSearchParams(next, { replace: true });
  };

  const showBanner = (message, tone = "success") => {
    setBannerMessage(message);
    setBannerTone(tone);
    window.setTimeout(() => setBannerMessage(""), 5000);
  };

  const handleStartRun = async () => {
    if (!selectedPlanId) return;
    setActionPending(true);
    try {
      await startVendorDirectoryCollectionRun(clientViewId, { planId: selectedPlanId });
      showBanner(t(`${COPY_PREFIX}.runQueued`));
      setShowStartPanel(false);
      setSelectedPlanId("");
      await loadRuns();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.runFailed`), "warning");
    } finally {
      setActionPending(false);
    }
  };

  const handleRetry = async (id) => {
    setActionPending(true);
    try {
      await retryVendorDirectoryCollectionRun(clientViewId, id);
      showBanner(t(`${COPY_PREFIX}.retrySuccess`));
      await loadRuns();
      if (runId) await loadRunDetail();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.actionError`), "warning");
    } finally {
      setActionPending(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(t(`${COPY_PREFIX}.cancelConfirm`))) return;
    setActionPending(true);
    try {
      await cancelVendorDirectoryCollectionRun(clientViewId, id);
      showBanner(t(`${COPY_PREFIX}.cancelSuccess`));
      await loadRuns();
      if (runId) await loadRunDetail();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.actionError`), "warning");
    } finally {
      setActionPending(false);
    }
  };

  if (runId) {
    if (!selectedRun && !errorMessage) {
      return (
        <div className="page-content cvd-admin">
          <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} />
        </div>
      );
    }
    if (selectedRun) {
      return (
        <div className="page-content cvd-admin">
          <CollectionRunDetail
            run={selectedRun}
            records={runRecords}
            recordsLoading={recordsLoading}
            basePath={basePath}
            clientViewId={clientViewId}
            selectedRecordId={selectedRecordId}
            onSelectRecord={setSelectedRecordId}
            onCloseRecord={closeSelectedRecord}
            t={t}
            locale={locale}
            onRetry={handleRetry}
            onCancel={handleCancel}
            actionPending={actionPending}
          />
        </div>
      );
    }
  }

  return (
    <div className="page-content cvd-admin cvd-collection-runs">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`)}</p>
        </div>
        <div className="cvd-admin__actions">
          <button type="button" className="cvd-btn cvd-btn--ghost" onClick={loadRuns}>
            {t(`${COPY_PREFIX}.refresh`)}
          </button>
          <Link className="cvd-btn cvd-btn--secondary" to={`${serviciosBase}/colecciones/planes`}>
            {t(`${COPY_PREFIX}.plansLink`)}
          </Link>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_EXECUTE}>
            <button
              type="button"
              className="cvd-btn cvd-btn--primary"
              onClick={() => setShowStartPanel((open) => !open)}
            >
              {t(`${COPY_PREFIX}.startRun`)}
            </button>
          </VendorDirectoryPermissionGate>
        </div>
      </div>

      {bannerMessage ? (
        <div className={`cvd-alert cvd-alert--${bannerTone === "warning" ? "warning" : "success"}`}>{bannerMessage}</div>
      ) : null}

      {showStartPanel ? (
        <section className="cvd-panel cvd-run-start">
          <h2>{t(`${COPY_PREFIX}.startRunTitle`)}</h2>
          <p className="muted">{t(`${COPY_PREFIX}.startRunHint`)}</p>
          <div className="cvd-run-start__form">
            <label className="cvd-field" htmlFor="collection-plan-select">
              {t(`${COPY_PREFIX}.selectPlan`)}
              <select
                id="collection-plan-select"
                value={selectedPlanId}
                onChange={(event) => setSelectedPlanId(event.target.value)}
              >
                <option value="">{t(`${COPY_PREFIX}.selectPlanPlaceholder`)}</option>
                {runnablePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} · {plan.sourceName || plan.connectorKey}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="cvd-btn cvd-btn--primary"
              disabled={!selectedPlanId || actionPending}
              onClick={handleStartRun}
            >
              {t(`${COPY_PREFIX}.startRun`)}
            </button>
          </div>
        </section>
      ) : null}

      {!loading && runs.length > 0 ? (
        <div className="cvd-metric-grid">
          <MetricCard label={t(`${COPY_PREFIX}.kpi.total`)} value={summary.total} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.active`)} value={summary.active} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.failed`)} value={summary.failed} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.staged`)} value={summary.staged} />
        </div>
      ) : null}

      <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterStatus`)}>
        {STATUS_FILTERS.map((value) => (
          <button
            key={value || "all"}
            type="button"
            className={`cvd-run-filter-chip${statusFilter === value ? " is-active" : ""}`}
            onClick={() => setStatusFilter(value)}
          >
            {value ? t(`${COPY_PREFIX}.status.${value}`, {}, value) : t(`${COPY_PREFIX}.allStatuses`)}
          </button>
        ))}
      </div>

      {hasActiveRuns ? <p className="cvd-run-auto-refresh muted">{t(`${COPY_PREFIX}.autoRefresh`)}</p> : null}

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} /> : null}

      {!loading && !errorMessage && runs.length === 0 ? (
        <section className="cvd-run-empty">
          <h2>{t(`${COPY_PREFIX}.emptyTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.emptyHint`)}</p>
          <Link className="cvd-btn cvd-btn--primary" to={`${serviciosBase}/colecciones/planes`}>
            {t(`${COPY_PREFIX}.emptyAction`)}
          </Link>
        </section>
      ) : null}

      {!loading && !errorMessage && runs.length > 0 ? (
        <div className="cvd-run-list">
          {runs.map((run) => (
            <article key={run.id} className="cvd-run-card">
              <div className="cvd-run-card__main">
                <div className="cvd-run-card__head">
                  <div>
                    <Link className="cvd-run-card__title" to={`${basePath}/${run.id}`}>
                      {run.planName || shortId(run.id)}
                    </Link>
                    <p className="cvd-run-card__sub">
                      {run.sourceName || "—"} · {formatDateTime(run.startedAt || run.createdAt, locale)}
                    </p>
                  </div>
                  <RunStatusBadge status={run.status} t={t} />
                </div>
                <div className="cvd-run-card__stats">
                  <span>
                    <strong>{run.recordsReceived ?? 0}</strong> {t(`${COPY_PREFIX}.table.records`).toLowerCase()}
                  </span>
                  <span>
                    <strong>{run.recordsStaged ?? 0}</strong> {t(`${COPY_PREFIX}.table.staged`).toLowerCase()}
                  </span>
                  <span>
                    <strong>{run.requestsSucceeded ?? 0}</strong>/
                    {run.requestsAttempted ?? 0} req
                  </span>
                </div>
              </div>
              <div className="cvd-run-card__actions">
                <Link className="cvd-btn cvd-btn--ghost" to={`${basePath}/${run.id}`}>
                  {t(`${COPY_PREFIX}.viewDetails`)}
                </Link>
                <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_RETRY}>
                  {["failed", "completed_with_errors", "cancelled"].includes(run.status) ? (
                    <button type="button" className="cvd-btn cvd-btn--secondary" onClick={() => handleRetry(run.id)}>
                      {t(`${COPY_PREFIX}.retry`)}
                    </button>
                  ) : null}
                </VendorDirectoryPermissionGate>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}
